import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import React, { useMemo } from 'react';
import { Highlight } from '@cube3/cubicle/src/core/atoms/Highlight';

const hitExp = /<em>.*?<\/em>/;
const emExp = /((?:<em>|<\/em>))/;

export interface MeiliHighlight {
  [key: string]: string | string[];
}

interface Props {
  /** hit data return by meili */
  hitObject: MeiliHighlight;
  /** current search query to highlight */
  query: string;
}

export const SearchHit = (props: Props) => {
  const { hitObject, query } = props;

  // get the key/value pair in the hitobject that best matches the hit
  const [key, value] = useMemo(() => {
    if (!hitObject) {
      return [];
    }

    // find the key in the hit object that contains the hit
    // NOTE: we prefer the key that contains the exact match, if there are multiple options
    const hitKey =
      Object.keys(hitObject).find((k) =>
        []
          .concat(hitObject[k])
          .some((h) => h?.includes?.(query) && hitExp.test(h))
      ) ||
      Object.keys(hitObject).find((k) =>
        [].concat(hitObject[k]).some((h) => hitExp.test(h))
      );

    const hitValue = hitObject[hitKey];

    return [hitKey, hitValue];
  }, [hitObject, query]);

  // generate a human readable label for the hit key
  const keyNice = useMemo(() => {
    if (!key) return '';
    switch (key) {
      case 'display_name':
        return 'name';
      default:
        return key.toLowerCase().replaceAll(/[-_]/g, ' ');
    }
  }, [key]);

  // generate highlighted text segment
  const valueNice = useMemo(() => {
    if (!value) {
      return null;
    }

    /**
     * We dont want extra parts that dont exist in the hitObject to be added to the highlight
     * Makes sure that only whatever is between the em tags gets displayed
     */
    const hitKey =
      Object.keys(hitObject).find((k) =>
        []
          .concat(hitObject[k])
          .some((h) => h?.includes?.(query) && hitExp.test(h))
      ) ||
      Object.keys(hitObject).find((k) =>
        [].concat(hitObject[k]).some((h) => hitExp.test(h))
      );

    const hitValue = hitObject[hitKey];
    //find the value between the em tags
    const stringHitValue = hitValue.toString();
    const startPosition = stringHitValue.indexOf('<em>') + '<em>'.length;
    const endPosition = stringHitValue.indexOf('</em>', startPosition);
    const valueBtwEm = stringHitValue.substring(startPosition, endPosition);

    // ususaly the value is a string
    if (typeof value === 'string') {
      // return highlight(value);
      return (
        <Highlight
          text={value.replace(/\n|<em>|<\/em>/g, '')}
          highlight={valueBtwEm}
          suffix={` in ${keyNice}`}
        />
      );
    }

    // but some data like tags can be represented as string array in the hitobject
    if (Array.isArray(value)) {
      const macthedValue =
        value.find((e) => e?.includes?.(query) && emExp.test(e)) ||
        value.find((e) => emExp.test(e));
      return (
        <Highlight
          text={macthedValue.replace(/\n|<em>|<\/em>/g, '') || ''}
          highlight={valueBtwEm}
          suffix={` in ${keyNice}`}
        />
      );
    }

    return <pre>{JSON.stringify(value, null, 2)}</pre>;
  }, [value, query, keyNice]);

  return valueNice;

  return <pre>{JSON.stringify(props.hitObject, null, 2)}</pre>;
};
